import { Box, PopperPlacementType } from '@mui/material';
import { useEffect, useState } from 'react';
import { Typography, type TypographyProps } from '@/UI/AppTypography/AppTypography';
import { Tooltip } from '@/UI/Tooltip/Tooltip';

interface EllipsisTextProps extends TypographyProps {
  maxWidth?: string;
  disableHoverListener?: boolean;
  delay?: number;
  enterNextDelay?: number;
  tooltipText?: string;
  placement?: PopperPlacementType;
  isMultiLine?: boolean;
  linesNumber?: number;
  // 'middle' keeps the last `tailLength` characters visible and elides the middle, so the start
  // and end of a long string both stay readable. Only takes effect for single-line string content.
  truncate?: 'end' | 'middle';
  tailLength?: number;
}

export const EllipsisText = ({
  children,
  maxWidth,
  disableHoverListener = false,
  delay = 100,
  enterNextDelay,
  tooltipText,
  placement = 'auto',
  isMultiLine = false,
  linesNumber = 2,
  truncate = 'end',
  tailLength = 8,
  sx,
  ...props
}: EllipsisTextProps) => {
  // Held in state rather than a ref so overflow detection re-attaches whenever the measured node is
  // replaced. `Tooltip` swaps between its MUI and FPL implementations when the `fpl-migration` flag
  // resolves from `undefined` to a boolean, which remounts this child: a ref-based observer would
  // stay bound to the now-detached node, never fire again, and leave `isOverflowing` stuck at false
  // — so the tooltip never appeared on hover in the app (WEA-3796).
  const [textNode, setTextNode] = useState<HTMLSpanElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Below `tailLength` characters there is no head to shrink, so fall back to end truncation,
  // which keeps its own ellipsis and overflow tooltip. Guards against an empty, unmeasured head.
  const isMiddle =
    truncate === 'middle' &&
    !isMultiLine &&
    typeof children === 'string' &&
    tailLength > 0 &&
    children.length > tailLength;

  useEffect(() => {
    const checkOverflow = () => {
      if (textNode) {
        if (isMultiLine) {
          setIsOverflowing(textNode.scrollHeight > textNode.clientHeight);
        } else {
          setIsOverflowing(textNode.scrollWidth > textNode.clientWidth);
        }
      }
    };
    // Run checkOverflow after layout using requestAnimationFrame
    const rafId = requestAnimationFrame(() => {
      checkOverflow();
    });

    let resizeObserver: ResizeObserver | undefined;
    if (textNode && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(textNode);
    }

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
    };
  }, [textNode, children, isMultiLine, isMiddle]);

  const getTooltipTitle = () => {
    if (tooltipText) {
      return tooltipText;
    }
    if (isOverflowing) {
      return children;
    }
    return undefined;
  };

  /** Tooltip clones the child; a block-level wrapper is required for flex + width:100% ellipsis. */
  const fillFlexRow = !isMultiLine && maxWidth === '100%';

  const content = isMiddle ? (
    // Split the string so the last `tailLength` characters stay pinned while the head shrinks
    // and shows the ellipsis. Head and tail join back to the full string, so copy and screen
    // readers still get the whole name. The head carries the ref used for overflow detection.
    // `whiteSpace: 'pre'` on both halves keeps a space that lands on the slice boundary from
    // being trimmed (trailing space of the head) or collapsed (leading space of the tail) — a
    // plain `nowrap` span drops it, which renders e.g. "Text Iterator" as "TextIterator".
    <Box component="span" sx={{ display: 'flex', minWidth: 0, maxWidth, ...sx }}>
      <Typography
        {...props}
        component="span"
        ref={setTextNode}
        noWrap
        overflow="hidden"
        textOverflow="ellipsis"
        sx={{ minWidth: 0, whiteSpace: 'pre' }}
      >
        {children.slice(0, -tailLength)}
      </Typography>
      <Typography
        variant={props.variant}
        color={props.color}
        component="span"
        noWrap
        sx={{ flexShrink: 0, whiteSpace: 'pre' }}
      >
        {children.slice(-tailLength)}
      </Typography>
    </Box>
  ) : (
    <Typography
      noWrap={!isMultiLine}
      textOverflow="ellipsis"
      overflow="hidden"
      maxWidth={maxWidth}
      sx={
        isMultiLine
          ? {
              ...sx,
              display: '-webkit-box',
              WebkitLineClamp: linesNumber,
              WebkitBoxOrient: 'vertical',
            }
          : { ...sx }
      }
      {...props}
      ref={setTextNode}
    >
      {children}
    </Typography>
  );

  return (
    <Tooltip
      title={getTooltipTitle()}
      enterDelay={delay}
      enterNextDelay={enterNextDelay}
      disableHoverListener={disableHoverListener}
      placement={placement}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ],
        },
      }}
    >
      {fillFlexRow ? (
        <Box component="span" sx={{ display: 'block', width: '100%', minWidth: 0, overflow: 'hidden' }}>
          {content}
        </Box>
      ) : (
        content
      )}
    </Tooltip>
  );
};
